/**
 * Admin SPA i18n setup (Phase 12).
 *
 * Mirrors the operator-side scaffolding (modules/web/src/i18n) but the
 * locale source is different: super-admins have no per-row preferred
 * locale (no tenant slug, no app_user). Instead the value lives in
 * {@code localStorage["admin.locale"]}, populated initially from
 * {@code navigator.language} and overridden by the locale switcher
 * in {@code AdminShell}.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import sv from "./sv.json";

const STORAGE_KEY = "admin.locale";

function resolveInitialLocale(): string {
  if (typeof window === "undefined") return "sv";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && (stored === "sv" || stored === "en")) return stored;
  const nav = window.navigator.language ?? "sv";
  return nav.toLowerCase().startsWith("en") ? "en" : "sv";
}

void i18n.use(initReactI18next).init({
  resources: {
    sv: { translation: sv },
    en: { translation: en },
  },
  lng: resolveInitialLocale(),
  fallbackLng: "sv",
  load: "languageOnly", // sv-SE → sv, en-US → en
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
});

/**
 * Persist a locale choice. Called by AdminShell's switcher. Returns the
 * canonical value that was actually applied (clamped to the supported
 * set; falls back to "sv" on anything unrecognised).
 */
export function setAdminLocale(locale: string): string {
  const canonical = locale === "en" ? "en" : "sv";
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, canonical);
  }
  void i18n.changeLanguage(canonical);
  if (typeof document !== "undefined") {
    document.documentElement.lang = canonical;
  }
  return canonical;
}

/** Set <html lang> on initial load. */
if (typeof document !== "undefined") {
  document.documentElement.lang = (i18n.language ?? "sv").split("-")[0];
}

export default i18n;
